import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import api from '../utils/api';

function Avatar({ name, color, size=28 }) {
  const i = (name||'?').split(' ').map(n=>n[0]).join('').slice(0,2).toUpperCase();
  return <div style={{width:size,height:size,borderRadius:'50%',background:color||'#5B5BD6',display:'flex',alignItems:'center',justifyContent:'center',fontSize:size*.34,fontWeight:'700',color:'#fff',flexShrink:0}}>{i}</div>;
}

function fmt(d) {
  if (!d) return '—';
  const dt = new Date(d+'T00:00:00');
  return String(dt.getDate()).padStart(2,'0')+'/'+String(dt.getMonth()+1).padStart(2,'0')+'/'+dt.getFullYear();
}

function todayISO() {
  return new Date().toISOString().slice(0,10);
}

export default function ResourcePage() {
  const { spaceId } = useParams();
  // Back to dashboard
  const backToDash = () => navigate(spaceId ? `/spaces/${spaceId}` : '/spaces');

  const navigate = useNavigate();
  const ctx = useOutletContext();
  const user = ctx?.user || {};
  const isAdmin = user.role === 'superadmin' || user.role === 'admin' || user.role === 'manager';

  const [entries, setEntries]       = useState([]);
  const [tasks, setTasks]           = useState([]);
  const [space, setSpace]           = useState(null);
  const [loading, setLoading]       = useState(true);
  const [showModal, setShowModal]   = useState(false);
  const [editEntry, setEditEntry]   = useState(null);
  const [deleteId, setDeleteId]     = useState(null);
  const [filterUser, setFilterUser] = useState('');
  const [filterTask, setFilterTask] = useState('');
  const [toast, setToast]           = useState(null);

  const [form, setForm] = useState({
    task_id:'', date:todayISO(), amount:'', unit:'H', notes:''
  });

  const showToast = (msg, type='success') => {
    setToast({msg,type});
    setTimeout(()=>setToast(null), 3000);
  };

  const load = useCallback(async (silent=false) => {
    if (!silent) setLoading(true);
    try {
      const [sp, ents, tks] = await Promise.allSettled([
        api.get(`/spaces/${spaceId}`),
        api.get(`/spaces/${spaceId}/time-entries`),
        api.get(`/spaces/${spaceId}/my-tasks`),
      ]);
      if (sp.status==='fulfilled' && sp.value) setSpace(sp.value);
      // Use functional updates so React batches without triggering intermediate blank state
      if (ents.status==='fulfilled' && Array.isArray(ents.value))
        setEntries(ents.value);
      if (tks.status==='fulfilled' && Array.isArray(tks.value))
        setTasks(tks.value);
    } catch(e) { console.error(e); }
    finally { if (!silent) setLoading(false); }
  }, [spaceId]);

  useEffect(()=>{ load(); },[load]);

  // Truly silent background sync — polls timestamp, reloads data invisibly
  // Uses React.startTransition so updates are low-priority and never block the UI
  useEffect(()=>{
    let lastTs = null;
    let active = true;
    let fails = 0;
    const poll = async () => {
      if (!active || !spaceId) return;
      try {
        const r = await api.get(`/spaces/${spaceId}/last-updated`).catch(()=>null);
        if (!r) { fails++; if (fails > 4) active = false; return; }
        fails = 0;
        if (lastTs === null) { lastTs = r.last_updated; return; }
        if (r.last_updated !== lastTs) {
          lastTs = r.last_updated;
          // Fetch silently and merge — never touch loading state
          try {
            const [ents, tks] = await Promise.allSettled([
              api.get(`/spaces/${spaceId}/time-entries`),
              api.get(`/spaces/${spaceId}/my-tasks`),
            ]);
            if (ents.status==='fulfilled' && Array.isArray(ents.value))
              setEntries(ents.value);
            if (tks.status==='fulfilled' && Array.isArray(tks.value))
              setTasks(tks.value);
          } catch(_) {}
        }
      } catch(_) { fails++; }
    };
    const iv = setInterval(poll, 20000);
    return () => { active = false; clearInterval(iv); };
  }, [spaceId]);

  useEffect(() => {
    function onAccessChanged() {
      // Silently reload resource data when phase access changes
      api.get(`/spaces/${spaceId}/time-entries`).then(d => { if(d) setEntries(Array.isArray(d)?d:[]); }).catch(()=>{});
      api.get(`/spaces/${spaceId}/my-tasks`).then(d => { if(d) setMyTasks(Array.isArray(d)?d:[]); }).catch(()=>{});
    }
    window.addEventListener('trajectory:access_changed', onAccessChanged);
    return () => window.removeEventListener('trajectory:access_changed', onAccessChanged);
  }, [spaceId]);

  function openNew() {
    setForm({task_id:'', date:todayISO(), amount:'', unit:'H', notes:''});
    setEditEntry(null);
    setShowModal(true);
  }

  function openEdit(entry) {
    setForm({
      task_id: entry.task_id||'',
      date: entry.date||todayISO(),
      amount: entry.unit==='D' ? String(entry.hours/8) : String(entry.hours),
      unit: 'H',
      notes: entry.notes||''
    });
    setEditEntry(entry);
    setShowModal(true);
  }

  async function saveEntry() {
    const hours = form.unit==='D' ? parseFloat(form.amount)*8 : parseFloat(form.amount);
    if (!form.task_id) return showToast('Please select a task','error');
    if (!form.amount || isNaN(hours) || hours<=0) return showToast('Hours must be greater than 0','error');
    if (form.date > todayISO()) return showToast('Future dates are not allowed','error');
    try {
      if (editEntry) {
        await api.put(`/time-entries/${editEntry.id}`, { task_id:form.task_id, date:form.date, hours, notes:form.notes });
        showToast('Entry updated');
      } else {
        await api.post(`/spaces/${spaceId}/time-entries`, { task_id:form.task_id, date:form.date, hours, notes:form.notes });
        showToast('Time logged successfully');
      }
      setShowModal(false);
      load(true); // silent reload — no flash
    } catch(e) { showToast(e?.error||'Failed to save','error'); }
  }

  async function deleteEntry() {
    try {
      await api.delete(`/time-entries/${deleteId}`);
      setDeleteId(null);
      showToast('Entry deleted');
      load(true); // silent
    } catch(e) { showToast(e?.error||'Failed','error'); }
  }

  // Filter entries
  const allUsers = [...new Map(entries.map(e=>[e.user_id,{id:e.user_id,name:e.full_name,color:e.avatar_color}])).values()];
  const filtered = entries.filter(e =>
    (!filterUser || e.user_id===filterUser) &&
    (!filterTask || e.task_id===filterTask)
  );

  // Summary stats
  const totalHours = filtered.reduce((s,e)=>s+(e.hours||0),0);
  const uniqueUsers = [...new Set(filtered.map(e=>e.user_id))].length;
  const uniqueTasks = [...new Set(filtered.map(e=>e.task_id))].length;
  const todayHours = filtered.filter(e=>e.date===todayISO()).reduce((s,e)=>s+(e.hours||0),0);

  // Per-task summary
  const taskSummary = {};
  filtered.forEach(e => {
    if (!taskSummary[e.task_id]) taskSummary[e.task_id] = { name:e.task_name, list:e.list_name, phase:e.phase_name, hours:0, users:{} };
    taskSummary[e.task_id].hours += e.hours||0;
    if (!taskSummary[e.task_id].users[e.user_id]) taskSummary[e.task_id].users[e.user_id] = { name:e.full_name, color:e.avatar_color, hours:0 };
    taskSummary[e.task_id].users[e.user_id].hours += e.hours||0;
  });

  if (loading) return (
    <div style={{flex:1,display:'flex',alignItems:'center',justifyContent:'center',color:'var(--muted)',gap:'10px',flexDirection:'column'}}>
      <div style={{width:16,height:16,border:'2px solid var(--border)',borderTopColor:'var(--brand)',borderRadius:'50%',animation:'spin .7s linear infinite'}}/>
      Loading resource data…
    </div>
  );

  return (
    <div style={{flex:1,overflowY:'auto',overflowX:'hidden',background:'var(--bg)',minHeight:0}}>
      {/* ← Back to Overview */}
      <button onClick={backToDash}
        style={{position:'fixed',top:10,left:200,zIndex:200,
          padding:'4px 12px',background:'white',border:'1px solid #E2E8F0',
          borderRadius:'16px',cursor:'pointer',fontSize:'11px',fontWeight:'600',
          color:'#64748B',boxShadow:'0 1px 3px rgba(0,0,0,.1)',
          display:'flex',alignItems:'center',gap:'4px',lineHeight:'1.4',
          transition:'all .15s'}}
        onMouseEnter={e=>{e.currentTarget.style.background='#5B5BD6';e.currentTarget.style.color='white';e.currentTarget.style.borderColor='#5B5BD6';}}
        onMouseLeave={e=>{e.currentTarget.style.background='white';e.currentTarget.style.color='#64748B';e.currentTarget.style.borderColor='#E2E8F0';}}>
        ← Overview
      </button>

      {/* Toast */}
      {toast&&(
        <div style={{position:'fixed',top:16,right:20,zIndex:9999,padding:'10px 18px',borderRadius:'8px',background:toast.type==='error'?'#DC2626':'#16A34A',color:'#fff',fontWeight:'600',fontSize:'13px',boxShadow:'0 4px 16px rgba(0,0,0,0.2)',animation:'fadeIn .2s'}}>
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div style={{padding:'14px 24px',borderBottom:'1px solid var(--border)',background:'var(--surface)',flexShrink:0,display:'flex',alignItems:'center',gap:'16px'}}>
        <div>
          <h1 style={{fontSize:'17px',fontWeight:'700',color:'var(--text)',margin:0}}>
            Resource Management
            {space&&<span style={{color:'var(--muted)',fontWeight:'400',fontSize:'14px'}}> — {space.name}</span>}
          </h1>
          <p style={{fontSize:'11px',color:'var(--muted)',margin:'2px 0 0'}}>Log and track actual time spent on tasks by team members</p>
        </div>
        <button className="btn btn-primary" onClick={openNew} style={{marginLeft:'auto',gap:'6px',display:'flex',alignItems:'center'}}>
          + Log Time
        </button>
      </div>

      <div style={{padding:'20px 24px',display:'flex',flexDirection:'column',gap:'20px'}}>

        {/* KPI Cards */}
        <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'12px'}}>
          {[
            {label:'Total Hours Logged', value:`${Math.round(totalHours*10)/10}h`, sub:'across all entries', color:'#5B5BD6'},
            {label:"Today's Hours",      value:`${Math.round(todayHours*10)/10}h`, sub:'logged today',       color:'#16A34A'},
            {label:'Active Members',     value:uniqueUsers,                         sub:'contributing',       color:'#0891B2'},
            {label:'Tasks Tracked',      value:uniqueTasks,                         sub:'with time logged',   color:'#D97706'},
          ].map(k=>(
            <div key={k.label} className="card" style={{padding:'16px 18px'}}>
              <div style={{fontSize:'11px',fontWeight:'700',textTransform:'uppercase',letterSpacing:'.6px',color:'var(--muted2)',marginBottom:'6px'}}>{k.label}</div>
              <div style={{fontSize:'28px',fontWeight:'800',color:k.color,letterSpacing:'-1px'}}>{k.value}</div>
              <div style={{fontSize:'11px',color:'var(--muted)',marginTop:'3px'}}>{k.sub}</div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="card" style={{padding:'12px 16px',display:'flex',gap:'12px',alignItems:'center',flexWrap:'wrap'}}>
          <span style={{fontSize:'12px',fontWeight:'600',color:'var(--muted)'}}>Filter:</span>
          <select className="select" value={filterUser} onChange={e=>setFilterUser(e.target.value)} style={{width:'180px',height:'32px',fontSize:'12px',padding:'4px 10px'}}>
            <option value="">All Members</option>
            {allUsers.map(u=><option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
          <select className="select" value={filterTask} onChange={e=>setFilterTask(e.target.value)} style={{width:'220px',height:'32px',fontSize:'12px',padding:'4px 10px'}}>
            <option value="">All Tasks</option>
            {tasks.map(t=><option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          {(filterUser||filterTask)&&(
            <button className="btn btn-sm" onClick={()=>{setFilterUser('');setFilterTask('');}}>✕ Clear</button>
          )}
          <span style={{marginLeft:'auto',fontSize:'12px',color:'var(--muted)'}}>{filtered.length} entries</span>
        </div>

        {/* Task Summary (per-task hours breakdown) */}
        {Object.keys(taskSummary).length > 0 && (
          <div className="card" style={{padding:'18px'}}>
            <div style={{fontSize:'11px',fontWeight:'700',textTransform:'uppercase',letterSpacing:'.7px',color:'var(--muted2)',marginBottom:'14px'}}>Hours by Task</div>
            <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
              {Object.entries(taskSummary).sort((a,b)=>b[1].hours-a[1].hours).map(([tid,ts])=>(
                <div key={tid}>
                  <div style={{display:'flex',alignItems:'center',gap:'10px',marginBottom:'5px'}}>
                    <div style={{flex:1}}>
                      <span style={{fontSize:'13px',fontWeight:'600',color:'var(--text)'}}>{ts.name}</span>
                      <span style={{fontSize:'11px',color:'var(--muted)',marginLeft:'8px'}}>{ts.phase} → {ts.list}</span>
                    </div>
                    <span style={{fontSize:'13px',fontWeight:'800',color:'#5B5BD6'}}>{Math.round(ts.hours*10)/10}h</span>
                  </div>
                  <div style={{display:'flex',gap:'6px',flexWrap:'wrap',marginBottom:'4px'}}>
                    {Object.values(ts.users).map((u,i)=>(
                      <div key={i} style={{display:'flex',alignItems:'center',gap:'4px',background:'var(--surface2)',borderRadius:'99px',padding:'2px 8px 2px 3px',border:'1px solid var(--border)'}}>
                        <Avatar name={u.name} color={u.color} size={18}/>
                        <span style={{fontSize:'11px',color:'var(--text)'}}>{u.name}</span>
                        <span style={{fontSize:'11px',fontWeight:'700',color:'#5B5BD6',marginLeft:'2px'}}>{Math.round(u.hours*10)/10}h</span>
                      </div>
                    ))}
                  </div>
                  <div style={{height:'6px',background:'var(--surface3)',borderRadius:'99px',overflow:'hidden'}}>
                    <div style={{height:'100%',width:`${Math.min(100,(ts.hours/totalHours)*100)}%`,background:'#5B5BD6',borderRadius:'99px',transition:'width .4s'}}/>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Time Entries Table */}
        <div className="card" style={{overflow:'hidden'}}>
          <div style={{padding:'14px 18px',borderBottom:'1px solid var(--border)',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
            <div style={{fontSize:'11px',fontWeight:'700',textTransform:'uppercase',letterSpacing:'.7px',color:'var(--muted2)'}}>Time Log</div>
          </div>
          {filtered.length===0 ? (
            <div style={{padding:'48px',textAlign:'center',color:'var(--muted)'}}>
              <div style={{fontSize:'32px',marginBottom:'10px'}}>⏱</div>
              <div style={{fontSize:'14px',fontWeight:'600',marginBottom:'6px'}}>No time entries yet</div>
              <div style={{fontSize:'12px'}}>Click "+ Log Time" to record your first entry</div>
            </div>
          ) : (
            <table style={{width:'100%',borderCollapse:'collapse'}}>
              <thead>
                <tr style={{background:'var(--surface2)'}}>
                  {['Member','Task','Phase / List','Date','Hours','Notes','Actions'].map(h=>(
                    <th key={h} style={{padding:'9px 14px',textAlign:'left',fontSize:'10px',fontWeight:'700',textTransform:'uppercase',letterSpacing:'.6px',color:'var(--muted2)',borderBottom:'1px solid var(--border)'}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((e,i)=>(
                  <tr key={e.id} style={{borderBottom:'1px solid var(--border)',background:i%2===0?'transparent':'var(--surface2)'}}>
                    <td style={{padding:'10px 14px'}}>
                      <div style={{display:'flex',alignItems:'center',gap:'7px'}}>
                        <Avatar name={e.full_name} color={e.avatar_color} size={26}/>
                        <span style={{fontSize:'12px',fontWeight:'600',color:'var(--text)'}}>{e.full_name}</span>
                      </div>
                    </td>
                    <td style={{padding:'10px 14px',fontSize:'12px',color:'var(--text)',maxWidth:'200px',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{e.task_name||'—'}</td>
                    <td style={{padding:'10px 14px'}}>
                      <div style={{fontSize:'11px',color:'var(--muted)'}}>{e.phase_name}</div>
                      <div style={{fontSize:'11px',color:'var(--muted2)'}}>{e.list_name}</div>
                    </td>
                    <td style={{padding:'10px 14px',fontSize:'12px',color:'var(--muted2)',whiteSpace:'nowrap'}}>{fmt(e.date)}</td>
                    <td style={{padding:'10px 14px'}}>
                      <span style={{fontSize:'13px',fontWeight:'800',color:'#5B5BD6'}}>{Math.round((e.hours||0)*10)/10}h</span>
                    </td>
                    <td style={{padding:'10px 14px',fontSize:'11px',color:'var(--muted)',maxWidth:'160px',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{e.notes||'—'}</td>
                    <td style={{padding:'10px 14px'}}>
                      {(e.user_id===user.id||isAdmin)&&(
                        <div style={{display:'flex',gap:'6px'}}>
                          <button onClick={()=>openEdit(e)} className="btn btn-sm" style={{fontSize:'10px',padding:'2px 8px'}}>Edit</button>
                          <button onClick={()=>setDeleteId(e.id)} className="btn btn-sm" style={{fontSize:'10px',padding:'2px 8px',color:'#DC2626',border:'1px solid #DC262640'}}>Delete</button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showModal&&(
        <div className="modal-overlay">
          <div className="modal" style={{width:'480px'}}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'20px'}}>
              <h3 style={{fontSize:'16px',fontWeight:'700',margin:0}}>{editEntry?'Edit Time Entry':'Log Time'}</h3>
              <button className="btn btn-sm" onClick={()=>setShowModal(false)}>✕</button>
            </div>

            {/* Task */}
            <div className="form-group">
              <label className="form-label">Task <span style={{color:'#DC2626'}}>*</span></label>
              <select className="select" value={form.task_id} onChange={e=>setForm(f=>({...f,task_id:e.target.value}))}>
                <option value="">— Select a task —</option>
                {tasks.reduce((acc,t) => {
                  const group = t.phase_name||'Other';
                  if (!acc.map[group]) { acc.map[group]=true; acc.groups.push(group); }
                  return acc;
                },{map:{},groups:[]}).groups.map(g=>(
                  <optgroup key={g} label={g}>
                    {tasks.filter(t=>(t.phase_name||'Other')===g).map(t=>(
                      <option key={t.id} value={t.id}>{t.parent_name?`↳ ${t.name}`:t.name} {t.list_name?`(${t.list_name})`:''}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>

            {/* Date */}
            <div className="form-group">
              <label className="form-label">Date <span style={{color:'#DC2626'}}>*</span></label>
              <input className="input" type="date" value={form.date} max={todayISO()}
                onChange={e=>setForm(f=>({...f,date:e.target.value}))}/>
              <div style={{fontSize:'10px',color:'var(--muted)',marginTop:'3px'}}>Future dates are not allowed</div>
            </div>

            {/* Time */}
            <div className="form-group">
              <label className="form-label">Time Worked <span style={{color:'#DC2626'}}>*</span></label>
              <div style={{display:'flex',gap:'8px'}}>
                <input className="input" type="number" min="0.1" step="0.5" placeholder="e.g. 2"
                  value={form.amount} onChange={e=>setForm(f=>({...f,amount:e.target.value}))}
                  style={{flex:1}}/>
                <select className="select" value={form.unit} onChange={e=>setForm(f=>({...f,unit:e.target.value}))} style={{width:'110px'}}>
                  <option value="H">Hours (H)</option>
                  <option value="D">Days (D)</option>
                </select>
              </div>
              {form.amount&&!isNaN(parseFloat(form.amount))&&(
                <div style={{fontSize:'11px',color:'#5B5BD6',marginTop:'4px',fontWeight:'600'}}>
                  = {Math.round((form.unit==='D'?parseFloat(form.amount)*8:parseFloat(form.amount))*10)/10} hours
                  {form.unit==='D'&&' (1 Day = 8 Hours)'}
                </div>
              )}
            </div>

            {/* Notes */}
            <div className="form-group">
              <label className="form-label">Notes <span style={{fontSize:'10px',fontWeight:'400',color:'var(--muted)'}}>optional</span></label>
              <textarea className="input" rows={2} placeholder="What did you work on?"
                value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))}
                style={{resize:'vertical'}}/>
            </div>

            <div style={{display:'flex',gap:'8px',justifyContent:'flex-end',paddingTop:'14px',borderTop:'1px solid var(--border)'}}>
              <button className="btn" onClick={()=>setShowModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveEntry}>
                {editEntry?'Save Changes':'Log Time'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteId&&(
        <div className="modal-overlay">
          <div className="modal" style={{width:'380px',textAlign:'center'}}>
            <div style={{fontSize:'32px',marginBottom:'12px'}}>🗑️</div>
            <h3 style={{fontSize:'15px',fontWeight:'700',marginBottom:'8px'}}>Delete Time Entry?</h3>
            <p style={{fontSize:'13px',color:'var(--muted)',marginBottom:'20px'}}>This action cannot be undone.</p>
            <div style={{display:'flex',gap:'8px',justifyContent:'center'}}>
              <button className="btn" style={{minWidth:'90px'}} onClick={()=>setDeleteId(null)}>Cancel</button>
              <button className="btn" style={{minWidth:'90px',background:'#DC2626',color:'#fff',border:'none'}} onClick={deleteEntry}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
